import {
  MODEL_CONTENT_TYPE,
  RADAR_CONTENT_TYPE,
  TERRAIN_CONTENT_TYPE,
  routes,
} from "@divespots/contracts";
import { ApiRequestError } from "./errors.ts";
import type { Transport } from "./transport.ts";

export function buildUrl(
  path: string,
  params: Record<string, string>,
  query: Record<string, string | undefined>,
): string {
  const filled = path.replace(/:([A-Za-z]+)/g, (_, key: string) => {
    const v = params[key];
    if (v === undefined) throw new Error(`missing path param ${key}`);
    return encodeURIComponent(v);
  });
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) if (v !== undefined && v !== "") qs.set(k, v);
  const s = qs.toString();
  return s ? `${filled}?${s}` : filled;
}

/** The `Accept` of a binary route: model files are glb, radar frames their own type, the rest terrain. */
const binaryType = (route: string) =>
  route === "getObjectModelFile"
    ? MODEL_CONTENT_TYPE
    : route === "radarFrame"
      ? RADAR_CONTENT_TYPE
      : TERRAIN_CONTENT_TYPE;

export const liveTransport: Transport = async ({
  route,
  params,
  query,
  body,
  rawBody,
  rawContentType,
  signal,
}) => {
  const def = routes[route];
  const url = buildUrl(def.path, params, query);
  let res: Response;
  try {
    res = await fetch(url, {
      method: def.method,
      headers: {
        ...(rawBody !== undefined
          ? { "content-type": rawContentType ?? "application/octet-stream" }
          : body === undefined
            ? {}
            : { "content-type": "application/json" }),
        accept:
          "response" in def && def.response === "binary" ? binaryType(route) : "application/json",
      },
      credentials: "same-origin",
      ...(rawBody !== undefined
        ? { body: rawBody }
        : body === undefined
          ? {}
          : { body: JSON.stringify(body) }),
      signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ApiRequestError("network", e instanceof Error ? e.message : "network error");
  }
  if ("response" in def && def.response === "binary" && res.ok) {
    return {
      status: res.status,
      binary: await res.arrayBuffer(),
      contentType: res.headers.get("content-type"),
    };
  }
  const text = await res.text();
  let json: unknown;
  try {
    json = text === "" ? undefined : JSON.parse(text);
  } catch {
    json = undefined;
  }
  const retryAfter = Number(res.headers.get("retry-after"));
  return {
    status: res.status,
    json,
    retryAfterSec:
      res.headers.has("retry-after") && Number.isFinite(retryAfter) ? retryAfter : null,
  };
};
